import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../api/client.js';
import type { SessionUser } from '../../api/types.js';
import { Alert, Field, Notice, PageHeader, fehlertext, useLoad } from '../shared/ui.js';

/**
 * Kunde anlegen -- eine Seite, drei Abschnitte, ein Speichern.
 *
 * Kein Wizard: Stammdaten sind Pflicht, Kondition und Zugang optional. Wer sie
 * weglaesst, hat einen Kunden mit dem sichtbaren Zustand "Kondition noch nicht
 * eingerichtet" -- kein stiller Nullrabatt, siehe Kundenliste.
 *
 * Die drei Schritte laufen als **drei** Aufrufe, nicht als einer: Kunde, dann
 * Kondition, dann Zugang. Scheitert Schritt 2 oder 3, ist der Kunde da und der
 * Anwender sieht, was fehlt -- statt eines halb geschriebenen Verbunds ohne
 * Rueckmeldung. Innerhalb jedes Schritts sichert der Server die Transaktion.
 */
export function AccountCreatePage({ user }: { user: SessionUser }): React.JSX.Element {
  const navigate = useNavigate();
  const intern = useLoad<{ items: Array<{ id: string; displayName: string }> }>(
    '/api/admin/accounts/internal-users',
  );

  const [f, setF] = useState({
    accountNumber: '',
    name: '',
    accountType: 'distributor',
    street: '',
    postalCode: '',
    city: '',
    salesOwnerId: user.role === 'sales' ? user.userId : '',
    mitKondition: true,
    validFrom: new Date().toISOString().slice(0, 10),
    baseDiscountPercent: '',
    mitZugang: true,
    email: '',
    displayName: '',
  });
  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<{
    accountId: string;
    initialPassword?: string;
    warnungen: string[];
  } | null>(null);

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const warnungen: string[] = [];
    try {
      const konto = await api.post<{ id: string }>('/api/admin/accounts', {
        accountNumber: f.accountNumber,
        name: f.name,
        accountType: f.accountType,
        address: { street: f.street, postalCode: f.postalCode, city: f.city },
        salesOwnerId: f.salesOwnerId === '' ? null : f.salesOwnerId,
      });
      let initialPassword: string | undefined;
      if (f.mitKondition && f.baseDiscountPercent !== '') {
        try {
          await api.post(`/api/admin/accounts/${konto.id}/conditions`, {
            validFrom: f.validFrom,
            baseDiscountPercent: Number(f.baseDiscountPercent),
          });
        } catch (err) {
          warnungen.push(`Kondition nicht angelegt: ${fehlertext(err)}`);
        }
      }
      if (f.mitZugang && f.email !== '') {
        try {
          const r = await api.post<{ initialPassword: string }>(
            `/api/admin/accounts/${konto.id}/users`,
            {
              email: f.email,
              displayName: f.displayName || f.name,
              role: 'account_user',
            },
          );
          initialPassword = r.initialPassword;
        } catch (err) {
          warnungen.push(`Zugang nicht angelegt: ${fehlertext(err)}`);
        }
      }
      setErgebnis({ accountId: konto.id, initialPassword, warnungen });
    } catch (err) {
      setError(fehlertext(err));
    } finally {
      setBusy(false);
    }
  }

  if (ergebnis !== null) {
    return (
      <>
        <PageHeader title="Kunde angelegt" />
        <div className="card">
          <p>
            <strong>{f.name}</strong> ({f.accountNumber}) wurde angelegt.
          </p>
          {ergebnis.warnungen.map((w) => (
            <p key={w} role="alert">
              {w}
            </p>
          ))}
          {ergebnis.initialPassword !== undefined && (
            <Notice>
              <strong>Einmalpasswort fuer {f.email}:</strong>{' '}
              <code className="secret">{ergebnis.initialPassword}</code>
              <p className="note">
                Wird nur jetzt angezeigt und nirgends gespeichert. Bitte dem Kunden sicher
                uebermitteln; er muss es bei der ersten Anmeldung aendern.
              </p>
            </Notice>
          )}
          <div className="row">
            <button
              type="button"
              className="primary"
              onClick={() => navigate(`/admin/kunden/${ergebnis.accountId}`)}
            >
              Zum Kunden
            </button>
            <button type="button" onClick={() => navigate('/admin/kunden')}>
              Zur Kundenliste
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Kunde anlegen"
        lead="Stammdaten sind Pflicht. Kondition und Zugang koennen jetzt oder spaeter angelegt werden."
      />
      <form onSubmit={speichern}>
        <section className="card">
          <h3>1. Stammdaten</h3>
          <div className="grid2">
            <Field
              label="Kundennummer"
              hint="In der Regel die ERP-Kundennummer. Muss eindeutig sein."
            >
              <input
                value={f.accountNumber}
                onChange={(e) => set('accountNumber', e.target.value)}
                required
                maxLength={40}
              />
            </Field>
            <Field label="Kundenname">
              <input
                value={f.name}
                onChange={(e) => set('name', e.target.value)}
                required
                maxLength={200}
              />
            </Field>
            <Field label="Kundentyp">
              <select
                value={f.accountType}
                onChange={(e) => set('accountType', e.target.value)}
              >
                <option value="distributor">Haendler</option>
                <option value="field_sales">Aussendienst</option>
                <option value="end_customer">Endkunde</option>
              </select>
            </Field>
            <Field
              label="Zustaendiger Vertrieb"
              hint="Organisatorisch, keine Zugriffsgrenze."
            >
              <select
                value={f.salesOwnerId}
                onChange={(e) => set('salesOwnerId', e.target.value)}
              >
                <option value="">– niemand –</option>
                {(intern.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Strasse">
              <input value={f.street} onChange={(e) => set('street', e.target.value)} />
            </Field>
            <Field label="PLZ">
              <input
                value={f.postalCode}
                onChange={(e) => set('postalCode', e.target.value)}
              />
            </Field>
            <Field label="Ort">
              <input value={f.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
          </div>
        </section>

        <section className="card">
          <h3>
            <label>
              <input
                type="checkbox"
                checked={f.mitKondition}
                onChange={(e) => set('mitKondition', e.target.checked)}
              />{' '}
              2. Kondition anlegen
            </label>
          </h3>
          {f.mitKondition ? (
            <div className="grid2">
              <Field label="Gueltig ab">
                <input
                  type="date"
                  value={f.validFrom}
                  onChange={(e) => set('validFrom', e.target.value)}
                />
              </Field>
              <Field
                label="Grundrabatt in %"
                hint="0 bis 40. Abweichungen je Produkttyp oder Werkstoff pflegt der Betreiber am Kunden."
              >
                <input
                  type="number"
                  min={0}
                  max={40}
                  step="0.5"
                  value={f.baseDiscountPercent}
                  onChange={(e) => set('baseDiscountPercent', e.target.value)}
                />
              </Field>
            </div>
          ) : (
            <p className="note">
              Ohne Kondition kann der Kunde keine Preise sehen und kein Angebot erhalten.
              Der Zustand ist in der Kundenliste sichtbar.
            </p>
          )}
        </section>

        <section className="card">
          <h3>
            <label>
              <input
                type="checkbox"
                checked={f.mitZugang}
                onChange={(e) => set('mitZugang', e.target.checked)}
              />{' '}
              3. Kundenzugang anlegen
            </label>
          </h3>
          {f.mitZugang && (
            <div className="grid2">
              <Field label="E-Mail-Adresse">
                <input
                  type="email"
                  value={f.email}
                  onChange={(e) => set('email', e.target.value)}
                />
              </Field>
              <Field label="Anzeigename" hint="Leer: Kundenname.">
                <input
                  value={f.displayName}
                  onChange={(e) => set('displayName', e.target.value)}
                />
              </Field>
            </div>
          )}
        </section>

        <Alert text={error} />
        <div className="row">
          <button type="submit" className="primary" disabled={busy}>
            Kunde anlegen
          </button>
          <button type="button" onClick={() => navigate('/admin/kunden')}>
            Abbrechen
          </button>
        </div>
      </form>
    </>
  );
}
